import { coachVoiceIssues } from '@platform/schema/coach-voice';
import { applyLanguageFixes, languageIssues } from '@platform/schema/language';

import { editableItems } from './edits';

import type { FieldWrite, ReviewScene } from './edits';
import type { LanguageIssue } from '@platform/schema/language';

/**
 * The language standard in the lesson editor — LANG-01.
 *
 * The console reads the same `languageIssues` the gate refuses with, over the same fields
 * the gate reads, so a lesson the panel calls clean is one the gate will publish.
 */

type Target =
  | { kind: 'lesson'; field: 'title' | 'summary' }
  | { kind: 'scene'; sceneId: string; field: 'prompt' | 'body' | 'hint' }
  | { kind: 'item'; sceneId: string; itemId: string }
  | { kind: 'question'; questionId: string };

export interface LessonText {
  /** Which field of which row, stable across re-reads of the lesson. */
  key: string;
  /** Where an editor finds it: "Scene 2, Hint". */
  where: string;
  text: string;
  target: Target;
}

export interface Flag {
  id: string;
  text: LessonText;
  /** A language issue, or a coach-voice finding carried in the same shape (SKILL-09). */
  issue:
    LanguageIssue | (Omit<LanguageIssue, 'rule'> & { rule: 'coach-voice' });
}

/** One press of a fix button, and what each field it touched read either side of it. */
export interface Applied {
  id: number;
  label: string;
  fields: { text: LessonText; before: string; after: string }[];
}

/** Every text a learner reads in the lesson, where the gate reads it. */
export function lessonTexts(lesson: {
  title: string;
  summary: string;
  scenes: readonly ReviewScene[];
}): LessonText[] {
  const own = (field: 'title' | 'summary', label: string): LessonText => ({
    key: `lesson:${field}`,
    where: `Lesson, ${label}`,
    text: lesson[field],
    target: { kind: 'lesson', field },
  });

  return [
    own('title', 'Title'),
    own('summary', 'Summary'),
    ...lesson.scenes.flatMap((scene) => sceneTexts(scene)),
  ];
}

function sceneTexts(scene: ReviewScene): LessonText[] {
  const at = (
    key: string,
    label: string,
    text: string | null | undefined,
    target: Target,
  ): LessonText[] =>
    text === null || text === undefined
      ? []
      : [
          {
            key: `${scene.id}:${key}`,
            where: `Scene ${String(scene.position)}, ${label}`,
            text,
            target,
          },
        ];
  const field = (name: 'prompt' | 'body' | 'hint'): Target => ({
    kind: 'scene',
    sceneId: scene.id,
    field: name,
  });

  return [
    ...at('prompt', 'Headline', scene.prompt, field('prompt')),
    ...(scene.bodyEnabled
      ? at('body', 'Subheadline', scene.body, field('body'))
      : []),
    ...(scene.hintEnabled ? at('hint', 'Hint', scene.hint, field('hint')) : []),
    ...editableItems(scene).flatMap((item, index) =>
      at(`item:${item.id}`, `Item ${String(index + 1)}`, item.label, {
        kind: 'item',
        sceneId: scene.id,
        itemId: item.id,
      }),
    ),
    ...('questionId' in scene &&
    typeof scene.questionId === 'string' &&
    'explanation' in scene
      ? at('explanation', 'Explanation', scene.explanation, {
          kind: 'question',
          questionId: scene.questionId,
        })
      : []),
  ];
}

export function lessonFlags(texts: readonly LessonText[]): Flag[] {
  return texts.flatMap((text) => {
    const language = languageIssues(text.text);
    // A word the language standard already flags keeps that flag, which has a fix.
    const flagged = (at: number, found: string): boolean =>
      language.some(
        (one) =>
          one.at === at && one.found.toLowerCase() === found.toLowerCase(),
      );

    return [
      ...language.map((issue) => ({
        id: `${text.key}@${String(issue.at)}:${issue.rule}`,
        text,
        issue,
      })),
      // Warnings only, whatever the copy linter calls them: the owner ruled that the
      // coach voice never blocks publishing (SKILL-09). Numbered, since two can share a
      // position.
      ...coachVoiceIssues(text.text)
        .filter(({ at, found }) => !flagged(at, found))
        .map(({ at, found, message }, index): Flag => ({
          id: `${text.key}@${String(at)}:coach-voice:${String(index)}`,
          text,
          issue: {
            rule: 'coach-voice',
            blocks: false,
            at,
            found,
            fix: null,
            message: `Coach voice: ${message}`,
          },
        })),
    ];
  });
}

/** The fields the chosen flags change, each with its text before and after. */
export function fixesFor(flags: readonly Flag[]): Applied['fields'] {
  const byText = new Map<string, Flag[]>();
  for (const flag of flags) {
    if (flag.issue.fix === null) continue;
    byText.set(flag.text.key, [...(byText.get(flag.text.key) ?? []), flag]);
  }

  return [...byText.values()].flatMap((chosen) => {
    const [first] = chosen;
    if (first === undefined) return [];

    const before = first.text.text;
    const after = applyLanguageFixes(
      before,
      chosen
        .map((flag) => flag.issue)
        .filter(
          (issue): issue is LanguageIssue => issue.rule !== 'coach-voice',
        ),
    );

    return after === before ? [] : [{ text: first.text, before, after }];
  });
}

/** The save that puts `next` into the field `text` names. */
export function writeFor(
  lessonId: string,
  text: LessonText,
  next: string,
): FieldWrite {
  const { target } = text;

  switch (target.kind) {
    case 'lesson':
      return { kind: 'lesson', lessonId, body: { [target.field]: next } };
    case 'scene':
      return {
        kind: 'scene',
        lessonId,
        sceneId: target.sceneId,
        body: { [target.field]: next },
      };
    case 'item':
      return {
        kind: 'scene',
        lessonId,
        sceneId: target.sceneId,
        body: { items: [{ id: target.itemId, label: next }] },
      };
    case 'question':
      return {
        kind: 'question',
        lessonId,
        questionId: target.questionId,
        explanation: next,
      };
  }
}

/**
 * The applied fixes that can still be taken back, newest first, and the text each field
 * returns to if they all are.
 *
 * A fix is revertible while every field it touched still reads what it left. A field
 * edited by hand since, or by a later fix, is the editor's text now, and a revert would
 * overwrite it. Reverting the later fix makes the earlier one revertible again.
 */
export function revertible(
  applied: readonly Applied[],
  texts: readonly LessonText[],
): { open: Applied[]; restore: Map<string, { text: LessonText; to: string }> } {
  const now = new Map(texts.map((text) => [text.key, text.text]));
  const expected = new Map(now);
  const open: Applied[] = [];

  for (const one of [...applied].reverse()) {
    const current = one.fields.every(
      (field) => now.get(field.text.key) === field.after,
    );
    if (current) open.push(one);

    const chained = one.fields.every(
      (field) => expected.get(field.text.key) === field.after,
    );
    if (chained)
      for (const field of one.fields)
        expected.set(field.text.key, field.before);
  }

  const restore = new Map<string, { text: LessonText; to: string }>();
  for (const text of texts) {
    const to = expected.get(text.key);
    if (to !== undefined && to !== text.text)
      restore.set(text.key, { text, to });
  }

  return { open, restore };
}
